import React from "react";
import { Users } from "lucide-react";

function AboutUs() {
  const milestones = [
    { year: "2014", event: "Milepa grundades" },
    { year: "2018", event: "Vi expanderade till tre behandlingsrum" },
    { year: "2019", event: "Två nya medarbetare anslöt till vårt team" },
    { year: "2020", event: "Vi nådde 5 000 nöjda kunder" },
    { year: "2020", event: "Vi utsågs till en av topp 10 bästa salonger i stan" },
    { year: "2024", event: "Vi firar 10 år av excellens" },
    { year: "2025", event: "Vi fortsätter växa med fokus på innovation, kvalitet och en ännu starkare kundupplevelse" }
  ];

  return (
    <div className="bg-white">
      {/* Hero */}
      <section 
        className="py-32 px-6 text-center"
        style={{ background: 'linear-gradient(135deg, rgba(98, 195, 214, 0.1) 0%, rgba(211, 154, 84, 0.1) 100%)' }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-light mb-6 text-gray-800">
            Om <span className="font-semibold" style={{ color: 'var(--primary)' }}>Oss</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Vår historia, våra värderingar och vår passion för skönhet
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-lg text-gray-600 leading-relaxed space-y-4">
            <p>
              Jag heter Julia och är auktoriserad hud- och spaterapeut samt specialist inom kosmetisk tatuering (Taffy). År 2014 grundade jag Salong Milepa i hjärtat av Helsingborg. Namnet Milepa kommer från mina barns namn – Milla, Edvin och Paula – vilket ger verksamheten en personlig och varm grund.
            </p>
            <p>
              I dag är vi ett engagerat team av tre hudterapeuter, och tillsammans driver vi Milepa med samma passion och omsorg som från första dagen. Vårt mål är att skapa en trygg och välkomnande plats där varje kund kan känna sig vacker, sedd och omhändertagen.
            </p>
            <p>
              För oss handlar hudvård inte bara om yttre skönhet, utan lika mycket om välmående, självkänsla och egen tid. Vi arbetar med moderna maskiner och avancerade tekniker och ser till att ständigt utveckla oss genom kurser och vidareutbildningar, för att alltid kunna erbjuda det senaste inom hudvård och skönhetsbehandlingar.
            </p>
            <p>
              Med vår långa erfarenhet, breda kompetens och genuina kärlek till yrket erbjuder vi behandlingar som kombinerar professionella resultat med en varm och personlig upplevelse.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <Users className="w-12 h-12 mb-4 mx-auto" style={{ color: 'var(--secondary)' }} />
          <h2 className="text-3xl md:text-4xl font-semibold mb-12 text-center" style={{ color: 'var(--secondary)' }}>
            Våra Värderingar
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "❤️",
                title: "Passion",
                description: "Vi älskar det vi gör och det syns i varje behandling"
              },
              {
                icon: "🎯",
                title: "Resultat",
                description: "Vi fokuserar på verkliga, mätbara resultat för våra kunder"
              },
              {
                icon: "🌟",
                title: "Excellens",
                description: "Vi strävar alltid efter den högsta kvaliteten i allt vi gör"
              }
            ].map((value, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white"
              >
                <div className="text-5xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold mb-16 text-center" style={{ color: 'var(--secondary)' }}>
            Vår Resa
          </h2>
          
          <div className="relative">
            {/* Central line */}
            <div 
              className="absolute left-1/2 top-0 bottom-0 w-1 transform -translate-x-1/2"
              style={{ backgroundColor: 'var(--primary)' }}
            />
            
            {/* Timeline items */}
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative">
                  {/* Circle on the line */}
                  <div 
                    className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full z-10"
                    style={{ backgroundColor: 'var(--secondary)' }}
                  />
                  
                  {/* Card - alternating left and right */}
                  <div className={`flex ${index % 2 === 0 ? 'justify-start pr-[51%]' : 'justify-end pl-[51%]'}`}>
                    <div className={`bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 w-full ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      <div className="font-bold text-xl mb-2 text-gray-900">
                        {milestone.year}
                      </div>
                      <p className="text-gray-700">
                        {milestone.event}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section 
        className="py-20 px-6"
        style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)' }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Bli en del av <span className="font-semibold">vår historia</span>
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Boka din tid idag och upplev Milepa-skillnaden
          </p>
          <a href="https://www.bokadirekt.se/places/salong-milepa-13263" target="_blank" rel="noopener noreferrer">
            <button 
              className="bg-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              style={{ color: 'var(--primary)' }}
            >
              Boka Nu
            </button>
          </a>
        </div>
      </section>
    </div>
  );
}

export default React.memo(AboutUs);